import React, { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import Select from "react-select";
import API from "../../../services/api";
import { Toast } from "../../../shared/Toast";
import TableLoader from "../../../shared/TableLoader";
import NoDataFound from "../../../shared/NoDataFound";
import { BrandsTypes, toLakhs } from "../../../utils/constants";
import BrandDetails from "./BrandDetails";

const L2LRevenues = () => {
  const [reportData, setReportData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const reviewReportsBase = "/review-reports";
  const [csvData, setCsvData] = useState([]);
  const csvLinkRef = useRef(null);
  const [brandType, setBrandType] = useState(2);
  const year = useMemo(() => new Date().getFullYear(), []);
  const [showBrandDetails, setShowBrandDetails] = useState(false);
  const [brandDetailsPayload, setBrandDetailsPayload] = useState(null);
  const detailUrl = "/review-reports/exclusiveBrandWiseDetails";

  const rows = useMemo(
    () => [
      { key: "w2n_actual", label: `W2N ${year - 1}`, isPlan: false },
      { key: "w2n_plan", label: `W2N ${year} plan`, isPlan: true },
    ],
    [year]
  );

  useEffect(() => {
    const fetchL2LRevenues = async () => {
      setIsLoading(true);
      try {
        const result = await API({
          url: `${reviewReportsBase}/getL2LRevenues`,
          method: "POST",
          data: { year, brand_type: brandType },
        });

        if (result.status === "SUCCESS") {
          setReportData(result.data || {});
        } else {
          setReportData({});
          Toast("error", result?.message || "Failed To Fetch L2L Revenues");
        }
      } catch (error) {
        setReportData({});
        Toast("error", "An error occurred");
      } finally {
        setIsLoading(false);
      }
    };

    fetchL2LRevenues();
  }, [year, brandType]);

  const getCellValue = (rowKey, columnKey) => {
    if (columnKey === "months_active") return "-";
    const raw = reportData?.[rowKey]?.[columnKey];
    if (raw === null || raw === undefined || raw === "") return "-";
    if (columnKey === "revenues") {
      return toLakhs(raw);
    }
    const numeric = Number(raw);
    if (Number.isNaN(numeric)) return raw;
    return Math.round(numeric);
  };

  const parseNumeric = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value === "string") {
      const cleaned = value.replace(/,/g, "").trim();
      if (cleaned === "") return null;
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const getRevenueTitle = (rowKey, columnKey) => {
    if (!["revenues", "avg_rev_per_day"].includes(columnKey)) return undefined;
    const raw = reportData?.[rowKey]?.[columnKey];
    const parsed = parseNumeric(raw);
    return parsed === null ? "-" : parsed;
  };

  const getCsvValue = (rowKey, columnKey) => {
    if (columnKey === "months_active") return "-";
    const raw = reportData?.[rowKey]?.[columnKey];
    if (raw === null || raw === undefined || raw === "") return "-";
    const numeric = Number(raw);
    if (Number.isNaN(numeric)) return raw;
    return Math.round(numeric);
  };

  const handlePrepareCSV = () => {
    if (!reportData || Object.keys(reportData).length === 0) return;
    const rowsForCsv = rows.map((row) => ({
      Metric: row.label,
      "No. of customers": row.isPlan ? "-" : getCsvValue(row.key, "customers"),
      [`${year - 1} Revenues`]: row.isPlan ? "-" : getCsvValue(row.key, "revenues"),
      "Insertions": row.isPlan ? "-" : getCsvValue(row.key, "insertions"),
      "No of months of activity": "-",
      "Av days of activity/month": row.isPlan ? "-" : getCsvValue(row.key, "avg_days_per_month"),
      "Av revenues/day of activity": row.isPlan ? "-" : getCsvValue(row.key, "avg_rev_per_day"),
    }));

    setCsvData(rowsForCsv);
  };

  useEffect(() => {
    if (csvData.length > 0) {
      csvLinkRef.current.link.click();
      setCsvData([]);
    }
  }, [csvData]);

  const getCellClass = (columnKey, isPlan) => {
    const base = "border px-2 py-1 text-center";
    const planClass = isPlan ? "text-gray-500" : "";
    return `${base} ${planClass}`.trim();
  };

  const formatDate = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleOpenBrandDetails = () => {
    const startDate = formatDate(new Date(year - 1, 0, 1));
    const endDate = formatDate(new Date(year - 1, 11, 31));
    setBrandDetailsPayload({ startDate, endDate, brand_type: brandType });
    setShowBrandDetails(true);
  };

  return (
    <div className="min-h-[50vh]">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="text-sm text-gray-700 font-semibold">L2L Revenues</div>
        <aside className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 min-w-[240px]">
            <span className="font-semibold whitespace-nowrap">Brand Type:</span>
            <Select
              className="min-w-[200px]"
              placeholder="Select Brand Type"
              value={BrandsTypes.find((option) => option.value === brandType) || null}
              onChange={(selected) => setBrandType(selected ? selected.value : null)}
              options={BrandsTypes}
              isClearable
            />
          </div>
          <CSVLink
            data={csvData}
            filename="l2l_revenues.csv"
            className="hidden"
            ref={csvLinkRef}
          />
          {Object.keys(reportData || {}).length > 0 && !isLoading && (
            <button
              onClick={handlePrepareCSV}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Download Report
            </button>
          )}
        </aside>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-[980px] w-full border border-gray-300 text-sm bg-white">
          <thead>
            <tr className="bg-gray-50 text-center">
              <th className="border px-2 py-2 text-left"></th>
              <th className="border px-2 py-2">No. of customers</th>
              <th className="border px-2 py-2">{year - 1} Revenues</th>
              <th className="border px-2 py-2">Insertions</th>
              <th className="border px-2 py-2">No of months of activity</th>
              <th className="border px-2 py-2">Av days of activity/month</th>
              <th className="border px-2 py-2">Av revenues/day of activity</th>
              <th className="border px-2 py-2">Missed</th>
              <th className="border px-2 py-2">Known</th>
              <th className="border px-2 py-2">Unknown</th>
            </tr>
          </thead>
          {isLoading ? (
            <TableLoader colspan="7" />
          ) : Object.keys(reportData || {}).length === 0 ? (
            <NoDataFound colspan="7" />
          ) : (
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td className={getCellClass("label", row.isPlan)}>{row.label}</td>
                  <td
                    className={`${getCellClass("customers", row.isPlan)} ${
                      row.isPlan ? "" : "text-blue-600  cursor-pointer"
                    }`.trim()}
                    onClick={() => !row.isPlan && handleOpenBrandDetails()}
                  >
                    {row.isPlan ? "-" : getCellValue(row.key, "customers")}
                  </td>
                  <td
                    className={getCellClass("revenues", row.isPlan)}
                    title={row.isPlan ? undefined : getRevenueTitle(row.key, "revenues")}
                  >
                    {row.isPlan ? "-" : getCellValue(row.key, "revenues")}
                  </td>
                  <td className={getCellClass("insertions", row.isPlan)}>
                    {row.isPlan ? "-" : getCellValue(row.key, "insertions")}
                  </td>
                  <td className={getCellClass("months_active", row.isPlan)}>-</td>
                  <td className={getCellClass("avg_days_per_month", row.isPlan)}>
                    {row.isPlan ? "-" : getCellValue(row.key, "avg_days_per_month")}
                  </td>
                  <td
                    className={getCellClass("avg_rev_per_day", row.isPlan)}
                    title={row.isPlan ? undefined : getRevenueTitle(row.key, "avg_rev_per_day")}
                  >
                    {row.isPlan ? "-" : getCellValue(row.key, "avg_rev_per_day")}
                  </td>
                  <td className={getCellClass("missed", row.isPlan)}>{getCellValue(row.key, "missed")}</td>
                  <td className={getCellClass("known", row.isPlan)}>{getCellValue(row.key, "known")}</td>
                  <td className={getCellClass("unknown", row.isPlan)}>{getCellValue(row.key, "unknown")}</td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
      <BrandDetails
        open={showBrandDetails}
        onClose={() => setShowBrandDetails(false)}
        url={detailUrl}
        payload={brandDetailsPayload}
        title={`W2N ${year - 1} Customers`}
      />
    </div>
  );
};

export default L2LRevenues;
